// The participants of a company, and the companies of a participant.
//
// A `deltagerRelation` entry holds the participant (`deltager`) and its
// `organisationer`. Each organisation has a FUNKTION attribute (for example
// "Direktion" or "EJERREGISTER"), and `medlemsData` with the member values:
// FUNKTION ("DIREKTØR") and EJERANDEL_PROCENT ("1.0" = 100 %). A value is
// current when its period has no `gyldigTil`.
//
// The rules are the same as the old CompanyView.vue: a relation is active when
// a current FUNKTION or EJERANDEL_PROCENT value is not "STIFTERE". The old
// view shared its role arrays between relations, so a role could show on the
// wrong participant. Here each relation has its own roles.

const OWNER_REGISTER = 'EJERREGISTER';

function isCurrent(value) {
  return !value?.periode?.gyldigTil && !value?.gyldigTil;
}

function roleLabel(value) {
  if (value === OWNER_REGISTER) return 'Ejer';
  const lower = String(value ?? '').toLocaleLowerCase('da');
  return lower.charAt(0).toLocaleUpperCase('da') + lower.slice(1);
}

function newestName(navne) {
  return Array.isArray(navne) && navne.length > 0 ? navne[navne.length - 1]?.navn ?? '' : '';
}

/** The newest registered name. `name` carries a status prefix, such as "[OPLØSTEFTERKONKURS]". */
export function companyName(doc) {
  return newestName(doc?.navne) || String(doc?.name ?? '').replace(/^\[[^\]]*\]\s*/, '');
}

// The page to link a participant to. ANDEN_DELTAGER ("other participant") is outside
// the register, for example a foreign company: it is in neither cvr nor cvrperson.
export function kindOf(deltager) {
  if (deltager.enhedstype === 'PERSON' || deltager._type === 'person') return 'person';
  if (deltager.enhedstype === 'ANDEN_DELTAGER') return 'other';
  return 'company';
}

// A CVR number has 8 digits.
function cvrNumber(value) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 10000000 && number <= 99999999 ? number : null;
}

/**
 * The CVR number of a participant: a company (VIRKSOMHED) carries it in
 * `forretningsnoegle`. Null for a person or another participant.
 */
export function participantCvr(deltager) {
  return kindOf(deltager) === 'company' ? cvrNumber(deltager?.forretningsnoegle) : null;
}

/** The page of a company: its CVR number, or its enhedsnummer when it has no CVR number. */
export function companyPath(company) {
  return `/Company/${cvrNumber(company?.cvr) ?? company?.enhedsNummer}`;
}

/**
 * The lookups for the id of a company URL, in order: `[[field, number]]`. An 8-digit
 * id is a CVR number first, because the site links with CVR numbers, and an old
 * enhedsnummer link second.
 */
export function companyLookups(id) {
  if (!/^\d+$/.test(String(id ?? ''))) return [];
  const number = Number(id);
  if (!Number.isSafeInteger(number)) return [];
  return String(id).length === 8 ? [['cvr', number], ['enhedsNummer', number]] : [['enhedsNummer', number]];
}

function describeRelation(rel) {
  const currentRoles = [];
  const allRoles = [];
  const organisationRoles = [];
  let active = false;
  let hasShare = false;
  let hadShare = false;
  let share = 0;

  for (const org of rel?.organisationer ?? []) {
    const orgFunction = (org.attributter ?? []).find((a) => a.type === 'FUNKTION');
    for (const value of orgFunction?.vaerdier ?? []) organisationRoles.push(value.vaerdi);
    const ownerRegister = org.hovedtype === 'REGISTER' && orgFunction?.vaerdier?.[0]?.vaerdi === OWNER_REGISTER;

    for (const member of org.medlemsData ?? []) {
      for (const att of member.attributter ?? []) {
        if (att.type !== 'FUNKTION' && att.type !== 'EJERANDEL_PROCENT') continue;
        for (const value of att.vaerdier ?? []) {
          const isShare = att.type === 'EJERANDEL_PROCENT' && ownerRegister;
          if (att.type === 'FUNKTION') allRoles.push(value.vaerdi);
          if (isShare) hadShare = true;
          if (!isCurrent(value)) continue;
          if (att.type === 'FUNKTION') currentRoles.push(value.vaerdi);
          if (isShare) {
            hasShare = true;
            share += Number(value.vaerdi) || 0;
          }
          if (value.vaerdi !== 'STIFTERE') active = true;
        }
      }
    }
  }

  let roles = active ? currentRoles : allRoles;
  if (active && hasShare) roles = [...roles, OWNER_REGISTER];
  if (roles.length === 0) roles = organisationRoles;

  const deltager = rel?.deltager ?? {};
  return {
    enhedsNummer: deltager.enhedsNummer,
    cvr: participantCvr(deltager),
    name: newestName(deltager.navne) || deltager.name || '',
    kind: kindOf(deltager),
    active,
    roles: [...new Set(roles.map(roleLabel))],
    share: hasShare && share > 0 ? Math.round(share * 10000) / 100 : null,
    wasOwner: !hasShare && hadShare,
  };
}

function split(list) {
  return {
    active: list.filter((item) => item.active),
    inactive: list.filter((item) => !item.active),
  };
}

/** The participants of one company, split into active and inactive. */
export function participants(company) {
  return split((company?.deltagerRelation ?? []).filter((rel) => rel?.deltager).map(describeRelation));
}

/** The companies in which `enhedsNummer` participates, split into active and inactive. */
export function engagements(companies, enhedsNummer) {
  const list = [];
  for (const company of companies ?? []) {
    const rel = (company.deltagerRelation ?? [])
      .find((r) => Number(r?.deltager?.enhedsNummer) === Number(enhedsNummer));
    if (!rel) continue;
    list.push({
      ...describeRelation(rel),
      enhedsNummer: company.enhedsNummer,
      cvr: cvrNumber(company.cvr),
      name: companyName(company),
      kind: 'company',
      status: company.cvrstatus ?? null,
    });
  }
  return split(list);
}

/**
 * The CVR URL to go to when the URL of the loaded company uses its enhedsnummer, or null.
 * The id must be the enhedsnummer of the loaded company: after a click on a link, the page
 * still holds the old company while the new one loads, and must not go back to it.
 */
export function companyRedirect(company, id) {
  if (!company || String(id) !== String(company.enhedsNummer)) return null;
  const path = companyPath(company);
  return path !== `/Company/${id}` ? path : null;
}
